import { inject, Injectable } from '@angular/core';
import {
  CRMMultiLocationActivityApiService,
  ListCrmObjectsRequestInterface,
  ListCrmObjectsResponseInterface,
  ListFieldOptionsRequestInterface,
  ListFiltersRequestInterface,
  ListFiltersResponseInterface,
  ListMultiLocationFieldOptionsRequest,
  ListMultiLocationFiltersRequest,
} from '@vendasta/crm';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { CrmMultilocationInjectionToken, MultiLocationContext } from '../../../tokens-and-interfaces';
import { ActivityObjectService } from './_activity_object.service';

@Injectable({ providedIn: 'root' })
export class MultiLocationActivityService extends ActivityObjectService {
  private multiLocationActivityService = inject(CRMMultiLocationActivityApiService);
  private multiLocationConfig = inject(CrmMultilocationInjectionToken);

  override listObjects(request: ListCrmObjectsRequestInterface): Observable<ListCrmObjectsResponseInterface> {
    return (this.multiLocationConfig.multiLocationContext$ ?? of({} as MultiLocationContext)).pipe(
      switchMap((multiLocationContext) => {
        const req = {
          namespaces: multiLocationContext.accountGroupsIds || [],
          sortBy: request.sortBy || [],
          filtersV2: request.filtersV2 || {},
          pagingOptions: request.pagingOptions || {},
          locale: this.locale(),
        };
        return this.multiLocationActivityService.listMultiLocationActivities(req);
      }),
      map((response) => {
        return {
          crmObjects: response.activities || [],
          pagingMetadata: response.pagingMetadata || {},
        } as ListCrmObjectsResponseInterface;
      }),
    );
  }

  override listObjectsFilters(request: ListFiltersRequestInterface): Observable<ListFiltersResponseInterface> {
    return (this.multiLocationConfig.multiLocationContext$ ?? of({} as MultiLocationContext)).pipe(
      switchMap((multiLocationContext) => {
        const req = {
          namespaces: multiLocationContext.accountGroupsIds || [],
          searchTerm: request.searchTerm || '',
          paging: request.paging || {},
          locale: this.locale(),
        } as ListMultiLocationFiltersRequest;
        return this.multiLocationActivityService.listMultiLocationActivityFilters(req);
      }),
      map((response) => {
        return {
          filters: response.filters || [],
          paging: response.paging || {},
        } as ListFiltersResponseInterface;
      }),
    );
  }

  override listFieldOptions(request: ListFieldOptionsRequestInterface): Observable<string[]> {
    return (this.multiLocationConfig.multiLocationContext$ ?? of({} as MultiLocationContext)).pipe(
      switchMap((multiLocationContext) => {
        const req = {
          namespaces: multiLocationContext.accountGroupsIds || [],
          fieldId: request.fieldId || '',
          pageSize: request.pageSize || 0,
          searchTerm: request.searchTerm || '',
        } as ListMultiLocationFieldOptionsRequest;
        return this.multiLocationActivityService.listMultiLocationActivityFieldOptions(req);
      }),
      map((response) => response.options || []),
    );
  }
}
